import { FC, useContext, useState } from 'react';
import { Box, Collapse } from '@mui/material';
import BasicExpand from '../common/BasicExpand';
import { useTranslation } from 'react-i18next';
import CampaignInfo from './CampaignInfo';
import CampaignSocials from './CampaignSocials';
import AdvertisementType from './AdvertisementType';
import ReleaseTime from './ReleaseTime';
import ReleaseDate from './ReleaseDate';
import TotalSelectable from './TotalSelectable';
import CampaignRules from './CampaignRules';
import { CampaignContext } from '../../contexts/CampaignContext';
import ContentTypeBox from './ContentTypeBox';
import { Info } from '@mui/icons-material';

type ICampaignInfoExpand = {
	action?: string;
	showExpanded?: boolean;
	editHeader?: boolean;
};

const CampaignInfoExpand: FC<ICampaignInfoExpand> = ({ showExpanded = false, action = 'read', editHeader = false }) => {
	const [expanded, setExpanded] = useState(showExpanded);
	const { fetchedCampaign, fetchedRules, selectedPlatform } = useContext(CampaignContext);

	const { t } = useTranslation();

	const handleExpand = () => {
		setExpanded(prev => !prev);
	};

	const basicRules = fetchedRules?.basic ?? '';
	const mediaRules = fetchedRules?.media?.map(rule => rule) || [];
	const mediaTypeRules = fetchedRules?.media_type?.map(rule => rule) || [];
	const mediaEntityRulesArray = fetchedRules?.media_entity?.map(rule => rule) || [];
	const flatMediaEntityRulesArray = mediaEntityRulesArray.flat(Infinity);
	const grayBoxRules = [basicRules, ...mediaRules];
	const blueBoxRules = [...mediaTypeRules, ...flatMediaEntityRulesArray];
	const allRules = [...grayBoxRules, ...blueBoxRules];

	const haveRules = allRules.length > 0;
	const showTotalSelectable = fetchedCampaign?.entities?.length! > 0 || fetchedCampaign?.packages?.length! > 0;

	const isAdvertising = fetchedCampaign?.campaign_type === 'advertising';
	return (
		<Box className={expanded ? 'mb-0' : 'mb-6'}>
			<BasicExpand title={isAdvertising ? t('advertising') : t('reportage')} expanded={expanded} onExpand={handleExpand} className={isAdvertising ? 'bg-primary-main' : 'bg-secondary-main'} />
			<Collapse in={expanded} className={`bg-background-default`}>
				<CampaignInfo mode={action === 'read' ? 'read' : 'create'} isAdvertising={isAdvertising} editHeader={editHeader} />
				<CampaignSocials mode={editHeader || action === 'read' ? 'read' : 'create'} />
				{(selectedPlatform === 'instagram' || selectedPlatform === 'influenceral') && <ContentTypeBox action={editHeader || action === 'read' ? 'read' : 'create'} />}
				<AdvertisementType mode={editHeader || action === 'read' ? 'read' : 'create'} />
				{basicRules && (
					<>
						<Box className="my-4 flex rounded-md border border-blue-400 bg-blue-50 px-6 py-5">
							<Info className="me-2 h-6 w-6 text-blue-500" />
							<Box className="text-base leading-7 text-text-secondary">
								<ol className="whitespace-pre-line ps-2 text-text-primary">{basicRules}</ol>
							</Box>
						</Box>
					</>
				)}
				{showTotalSelectable && <TotalSelectable mode={editHeader || action === 'read' ? 'read' : 'create'} />}
				<ReleaseTime isCreateMode={editHeader || action === 'read' ? false : true} />
				<ReleaseDate isCreateMode={editHeader || action === 'read' ? false : true} />
				{haveRules && <CampaignRules rules={fetchedRules} />}
			</Collapse>
		</Box>
	);
};

export default CampaignInfoExpand;
